// src/components/VerifyOtp.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Key, ArrowLeft, AlertCircle } from 'lucide-react';
import axios from 'axios';

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // State for resend functionality
  const [resendCooldown, setResendCooldown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
    }
  }, [email, navigate]);

  // Cooldown timer effect
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown(prevTime => prevTime - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const yellowGradient = 'linear-gradient(135deg, #F2C231 0%, #C69A32 100%)';

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!email) {
      setErrorMessage("Email not found. Please go back to signup.");
      setIsLoading(false);
      return;
    }
    if (otp.length !== 6) {
      setErrorMessage("Please enter a 6-digit OTP.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://api.cafpraticheonline.it/api/auth/verify-otp', {
        email,
        otp
      });

      if (response.data.success) {
        setSuccessMessage('Signup successful! Redirecting to login...');

        const consent = localStorage.getItem('cookieConsent');

        if (consent === 'true') {
          localStorage.setItem('jwtToken', response.data.jwtToken);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } else {
          sessionStorage.setItem('jwtToken', response.data.jwtToken);
          sessionStorage.setItem('user', JSON.stringify(response.data.user));
        }

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1500);
      } else {
        setErrorMessage(response.data.message || 'OTP verification failed. Please try again.');
      }

    } catch (error) {
      console.error("OTP verification error:", error.response ? error.response.data : error.message);
      setErrorMessage(error.response?.data?.message || 'An unexpected error occurred during OTP verification.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setResendMessage('');

    try {
      const response = await axios.post('https://api.cafpraticheonline.it/api/auth/resend-otp', { email });

      if (response.data.success) {
        setResendMessage('A new OTP has been sent to your email.');
        setResendCooldown(60); // Reset the timer
      } else {
        setResendMessage(response.data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error("Resend OTP error:", error.response ? error.response.data : error.message);
      setResendMessage(error.response?.data?.message || 'An error occurred while resending the OTP.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center relative">
          <Link
            to="/signup"
            className="absolute -top-4 -left-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </Link>

          <div
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ background: yellowGradient }}
          >
            <Key className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Account</h2>
          <p className="text-gray-600">
            Please enter the 6-digit code sent to <span className="font-semibold text-gray-800">{email || 'your email'}</span>.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleVerifyOtp}>
          <div className="space-y-4">
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="otp"
                  type="text"
                  required
                  value={otp}
                  onChange={handleOtpChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-center tracking-widest text-xl"
                  placeholder="------"
                  maxLength="6"
                />
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="flex items-center text-red-600 text-sm mt-2">
              <AlertCircle className="h-4 w-4 mr-2" />
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="flex items-center text-green-600 text-sm mt-2">
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            style={{ background: yellowGradient }}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              'Verify Account'
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <p className="text-gray-600">
            Didn't receive the code?{' '}
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isResending || resendCooldown > 0}
              className="font-medium text-yellow-600 hover:text-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Resending...' : (resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP')}
            </button>
          </p>
          {resendMessage && (
            <div className={`mt-2 text-sm ${resendMessage.includes('sent') ? 'text-green-600' : 'text-red-600'}`}>
              {resendMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;